import { Component } from '@theme/component';
import { ThemeEvents } from '@theme/events';

/**
 * Activates a media slide on click (visual emphasis + reveals its buy button),
 * and keeps the active slide in sync with whichever video is currently playing.
 *
 * Relies on the theme's own <deferred-media> for lazy-loading/playing videos and
 * cross-slide "pause the others" behavior (it already listens for
 * `ThemeEvents.mediaStartedPlaying` globally) - this component only tracks which
 * slide should look active and manages its own mute button.
 *
 * @extends {Component}
 */
class CleogeneMediaSlider extends Component {
  #abortController = new AbortController();

  connectedCallback() {
    super.connectedCallback();
    const signal = this.#abortController.signal;

    this.addEventListener('click', this.#handleClick, { signal });
    this.addEventListener(ThemeEvents.mediaStartedPlaying, this.#handleMediaStartedPlaying, { signal });

    const firstSlide = this.querySelector('.cleogene-media-slider__slide');
    if (firstSlide) this.#setActiveSlide(firstSlide);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController.abort();
  }

  /** @param {MouseEvent} event */
  #handleClick = (event) => {
    const target = /** @type {HTMLElement} */ (event.target);

    const muteButton = target.closest('[data-media-mute]');
    if (muteButton instanceof HTMLElement) {
      event.preventDefault();
      this.#toggleMute(muteButton);
      return;
    }

    const activator = target.closest('[data-media-activate]');
    if (activator instanceof HTMLElement) {
      const slide = activator.closest('.cleogene-media-slider__slide');
      if (slide instanceof HTMLElement) this.#setActiveSlide(slide);
    }
  };

  /** @param {Event} event */
  #handleMediaStartedPlaying = (event) => {
    const resource = /** @type {CustomEvent<{ resource: HTMLElement }>} */ (event).detail?.resource;
    const slide = resource?.closest?.('.cleogene-media-slider__slide');
    if (slide instanceof HTMLElement) this.#setActiveSlide(slide);
  };

  /** @param {HTMLElement} slide */
  #setActiveSlide(slide) {
    const slides = this.querySelectorAll('.cleogene-media-slider__slide');

    for (const candidate of slides) {
      const isActive = candidate === slide;
      candidate.classList.toggle('is-active', isActive);

      const buyButton = candidate.querySelector('[data-media-buy]');
      if (buyButton instanceof HTMLElement) buyButton.hidden = !isActive;

      const muteButton = candidate.querySelector('[data-media-mute]');
      const video = candidate.querySelector('video');
      if (muteButton instanceof HTMLElement) {
        muteButton.hidden = !(isActive && video);
      }
    }
  }

  /** @param {HTMLElement} muteButton */
  #toggleMute(muteButton) {
    const slide = muteButton.closest('.cleogene-media-slider__slide');
    const video = slide?.querySelector('video');
    if (!video) return;

    video.muted = !video.muted;
    muteButton.classList.toggle('is-unmuted', !video.muted);
    muteButton.setAttribute('aria-label', video.muted ? 'Activer le son' : 'Couper le son');
  }
}

if (!customElements.get('cleogene-media-slider')) {
  customElements.define('cleogene-media-slider', CleogeneMediaSlider);
}
